/* eslint-disable react/prop-types */
import React, { memo, useState, useEffect, useCallback, useContext } from 'react';
import { useSelector } from 'react-redux';

import { TextContext } from '@/utils/contexts/TextContext';

import { formatTimeDate } from "@/utils/functions/formatTimeDate";

import RoundIcon from '@/views/pages/Letter/elements/LetterPopover/RoundIcon/RoundIcon';

import SingleChangeEditModule from '@/views/pages/Letter/elements/LetterPopover/SingleChangeEditModule';

import styles from '@/views/pages/Letter/elements/LetterPopover/LetterPopover.module.scss';

const SingleChange = ({
	change,
	highlightChangeFunction,
}) => {
	const { dict } = useSelector((state) => state.app);
	const { contextState, contextDispatch } = useContext(TextContext);
	const [ showMotivation, setShowMotivation ] = useState(false);

	const [ editMode, setEditMode ] = useState(false);

	const [ addingToDictionary, setAddingToDictionary ] = useState(false);

	const [ typeOfChange, setTypeOfChange ] = useState('change');

	useEffect(() => {
		if (change.content === null && change.footnote === null) {
			setTypeOfChange('removal');
		} else if (change.footnote !== null) {
			setTypeOfChange('footnote');
		} else {
			setTypeOfChange('change');
		}
	}, [ change ]);

	useEffect(() => {
		setShowMotivation(false);
	}, [ change ]);

	useEffect(() => {
		if (contextState.addToExistingMode && editMode) {
			setEditMode(false);
		}
	}, [ contextState.addToExistingMode ]);

	const onEditSingleChange = useCallback(() => {
		setEditMode(true);
	}, []);

	const onEditCancel = useCallback(() => {
		setEditMode(false);
	}, []);

	useEffect(() => {
		if (contextState.eventObject && contextState.eventObject.type === 'editAdded') {
			// when edited change successfully added to api:
			// setSendingChange(false);
			setEditMode(false);
			contextDispatch({
				type: 'setPopoverMode', payload: {
					popoverMode: 0,
				},
			});
			contextDispatch({ type: 'resetEventObject' });
		}
	}, [ contextState.eventObject ]);

	const onEditAccepted = useCallback((change, changeId) => {
		// setSendingChange(true);
		contextDispatch({
			type: 'handleChangeEditConfirmed', payload: {
				...change, changeId,
			},
		});
		// eslint-disable-next-line
    }, []);

	return (
		<div
			className={ styles['letter-popover--change'] }
			style={ 
				contextState.currentSelectedChangeId === change.id ? { borderColor:  `#${change.colour}` } : {}
			}
			onMouseEnter={ () => { highlightChangeFunction(change.id); } }
			onMouseLeave={ () => { highlightChangeFunction(contextState.currentSelectedChangeId); } }
		>
			<div className={ styles['letter-popover__change--header'] }>
				<div>
					<RoundIcon
						bgColor={ `#${change.colour}` }
						isAuto={ change.auto }
					/>
				</div>
				<div className={ styles['letter-popover__change--owner'] }>
					{(change.auto) ? (
						<div className={ styles['letter-popover__change--owner-source-type'] }>
							{ dict.letter.popover.automaticChange }
						</div>
					) : (
						<>
							<div className={ styles['letter-popover__change--owner-source-type'] }>
								{ dict.letter.popover.redactorChange }
							</div>
							<div>
								<div className={ styles['letter-popover__change--owner-name'] }>{change.owner.forename} {change.owner.surname}</div>
								<div className={ styles['letter-popover__change--owner-email'] }>{change.owner.email}</div>
							</div>
						</>
					)}
				</div>
				<div className={ styles['letter-popover__change--date'] }>
					{formatTimeDate(change.modifiedOn)}
				</div>
			</div>
			<div className={ !editMode ? styles['letter-popover__change--module-title'] : '' }>
				{typeOfChange === 'removal' && dict.letter.popover.contentRemoved}
				{typeOfChange === 'footnote' && dict.letter.popover.contentMoved}
				{typeOfChange === 'change' && dict.letter.popover.contentChanged}
			</div>
			{!editMode && typeOfChange === 'change' && (
				<div
					className={ styles['letter-popover__change--content'] }
					dangerouslySetInnerHTML={ { __html: change.content } }
				/>
			)}
			{!editMode && typeOfChange === 'footnote' && (
				<div 
					className={ styles['letter-popover__change--content'] }
					dangerouslySetInnerHTML={ { __html: change.footnote } }
				/>
			)}
			{!editMode && typeOfChange === 'removal' && (
				<div 
					className={ styles['letter-popover__change--content'] }
					dangerouslySetInnerHTML={ { __html: change.text } }
					style={ { textDecoration: 'line-through' } }
				/>
			)}
			{!editMode && change.motivation && typeOfChange !== 'removal' && !showMotivation && (
				<div className={ styles['letter-popover__change--module'] }>
					<div
						className={ styles['letter-popover__change--module-btn'] }
						onClick={ () => setShowMotivation(true) }
					>
						{dict.letter.popover.showReason}
						<i className='icon icon-arrow-down-full' />
					</div>
				</div>
			)}
			{!editMode && change.motivation && typeOfChange !== 'removal' && showMotivation && (
				<div className={ styles['letter-popover__change--module'] }>
					<div 
						className={ styles['letter-popover__change--module-btn'] }
						onClick={ () => setShowMotivation(false) }
					>
						{dict.letter.popover.hideReason}
						<i className='icon icon-arrow-up-full' />
					</div>
					<div
						dangerouslySetInnerHTML={ { __html: change.motivation } }
					/>
				</div>
			)}
			<div className={ styles['letter-popover__change--actions'] }>
				{ !editMode && !change.auto && change.rights.canAddToDictionary && typeOfChange === 'change' && (
					!change.inDictionary ? (
						<button 
							className={ styles['letter-popover-action-btn'] }
							onClick={ () => {
								setAddingToDictionary(true);
								contextDispatch({
									type: 'addChangeToDict', payload: {
										changeId: change.id,
									},
								});
							} }
						>
							{addingToDictionary ?
								dict.letter.popover.addingToDict :
								`+ ${dict.letter.popover.addToDict}`}
						</button>
					) : (
						<div style={ { lineHeight: '18px' } }>{ dict.letter.popover.addedToDict }</div>
					)
				)}
				{ !editMode && change.rights.canEdit && typeOfChange !== 'removal' && (
					<button
						className={ styles['letter-popover-action-btn'] }
						onClick={ () => {
							onEditSingleChange();
						} }
					>
						<i className="icon-edit" />
						{dict.letter.popover.editBtn}
					</button>
				)}
				{ !editMode && change.rights.canDelete && (
					<button 
						className={ styles['letter-popover-action-btn'] }
						onClick={ () => {
							contextDispatch({
								type: 'showModalDeleteChange', payload: {
									changeId: change.id,
								},
							});
						} }
					>
						<i className="icon-close" />
						{ typeOfChange === 'footnote' ? dict.letter.popover.reverseMove : dict.letter.popover.deleteBtn }
					</button>
				)}
			</div>
			{editMode && (
				<SingleChangeEditModule
					onEditAccepted={ onEditAccepted }
					onEditCancel={ onEditCancel }
					changeId={ change.id }
					change={ change }
					changeType={ typeOfChange }
				/>
			)}

		</div>
	);
};
export default memo(SingleChange);